import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import * as dayPickerLocales from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { useTranslation } from '../i18n';
import { Tooltip } from './Tooltip';
import { FieldError } from './FieldError';
import { formatDate } from '../formatDate';
import {
  MAX_RENTAL_END_DAYS_AHEAD,
  UnavailableRange,
  addDaysYmd,
  fetchProductAvailability,
  formatLocalDateToYmd,
  isDateInUnavailableRanges,
  parseYmdToLocalDate,
  rangeOverlapsUnavailable,
  todayYmd,
} from '../availability';

interface RentalDateRangePickerProps {
  productId: number;
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  disabled?: boolean;
  rangeError?: string;
  onRangeError?: (message: string) => void;
}

export const RentalDateRangePicker: React.FC<RentalDateRangePickerProps> = ({
  productId,
  startDate,
  endDate,
  onChange,
  disabled = false,
  rangeError = '',
  onRangeError,
}) => {
  const { t, lang } = useTranslation();
  const dpLocales = dayPickerLocales as unknown as { cs: object; enGB: object };
  const locale = lang === 'cs' ? dpLocales.cs : dpLocales.enGB;
  const [ranges, setRanges] = useState<UnavailableRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [hoveredYmd, setHoveredYmd] = useState('');

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // The lender's share is held on their Stripe balance until the rental ends, which
  // Stripe limits, so rentals must end within MAX_RENTAL_END_DAYS_AHEAD days.
  const lastBookableDay = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + MAX_RENTAL_END_DAYS_AHEAD);
    return d;
  }, [today]);

  const endMonth = useMemo(() => {
    const d = new Date(lastBookableDay);
    d.setDate(1);
    return d;
  }, [lastBookableDay]);

  const loadAvailability = useCallback(async (fromYmd: string, toYmd: string) => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await fetchProductAvailability(productId, fromYmd, toYmd);
      setRanges(data);
    } catch {
      setFetchError(t('rental.availability_load_error'));
      setRanges([]);
    } finally {
      setLoading(false);
    }
  }, [productId, t]);

  useEffect(() => {
    const from = todayYmd();
    const to = addDaysYmd(from, MAX_RENTAL_END_DAYS_AHEAD);
    loadAvailability(from, to);
  }, [loadAvailability]);

  const selectedRange: DateRange | undefined = useMemo(() => {
    const from = startDate ? parseYmdToLocalDate(startDate) : undefined;
    const to = endDate ? parseYmdToLocalDate(endDate) : undefined;
    if (!from && !to) return undefined;
    return { from, to };
  }, [startDate, endDate]);

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange('', '');
      onRangeError?.('');
      return;
    }

    const fromYmd = formatLocalDateToYmd(range.from);
    const toYmd = range.to ? formatLocalDateToYmd(range.to) : '';

    if (range.to && rangeOverlapsUnavailable(fromYmd, toYmd, ranges)) {
      onRangeError?.(t('rental.range_unavailable'));
      onChange('', '');
      return;
    }

    onRangeError?.('');
    onChange(fromYmd, toYmd);
  };

  const isDayDisabled = (date: Date) => {
    if (date < today || date > lastBookableDay) return true;
    return isDateInUnavailableRanges(formatLocalDateToYmd(date), ranges);
  };

  const previewEndYmd = startDate && !endDate && hoveredYmd > startDate ? hoveredYmd : '';

  const dayModifiers = useMemo(() => ({
    unavailable: (date: Date) =>
      isDateInUnavailableRanges(formatLocalDateToYmd(date), ranges),
    // Shows the range the next click would select while the end is being picked.
    preview: (date: Date) => {
      if (!previewEndYmd) return false;
      const ymd = formatLocalDateToYmd(date);
      return ymd > startDate && ymd <= previewEndYmd;
    },
  }), [ranges, startDate, previewEndYmd]);

  // The calendar is a single range picker, so the field it is filling next is highlighted.
  const activeField: 'start' | 'end' = startDate && !endDate ? 'end' : 'start';

  const renderField = (field: 'start' | 'end', label: string, help: string, value: string) => (
    <div
      className={[
        'rental-date-picker__field',
        activeField === field && !disabled ? 'rental-date-picker__field--active' : '',
        field === 'end' && rangeError ? 'rental-date-picker__field--error' : '',
      ].filter(Boolean).join(' ')}
    >
      <span className="rental-date-picker__field-label">
        {label}{' '}
        <Tooltip content={help} />
      </span>
      <span className={`rental-date-picker__field-value${value ? '' : ' rental-date-picker__field-value--empty'}`}>
        {value
          ? formatDate(parseYmdToLocalDate(value) ?? value, lang, { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' })
          : t('rental.date_field_empty')}
      </span>
    </div>
  );

  return (
    <div className="rental-date-picker">
      <div className="rental-date-picker__fields" aria-live="polite">
        {renderField('start', t('rental.start_date'), t('rental.start_date_help'), startDate)}
        {renderField('end', t('rental.end_date'), t('rental.end_date_help'), endDate)}
      </div>

      <div className={`rental-date-picker__calendar${disabled ? ' rental-date-picker__calendar--disabled' : ''}`}>
        {loading && (
          <p className="rental-date-picker__loading text-muted" aria-live="polite">
            {t('rental.availability_loading')}
          </p>
        )}
        {fetchError && (
          <div className="alert alert-error rental-date-picker__fetch-error">{fetchError}</div>
        )}
        <DayPicker
          mode="range"
          locale={locale}
          selected={selectedRange}
          onSelect={handleSelect}
          disabled={disabled ? true : isDayDisabled}
          excludeDisabled
          resetOnSelect
          onDayMouseEnter={date => setHoveredYmd(formatLocalDateToYmd(date))}
          onDayMouseLeave={() => setHoveredYmd('')}
          startMonth={today}
          endMonth={endMonth}
          modifiers={dayModifiers}
          modifiersClassNames={{
            unavailable: 'rdp-day--unavailable',
            preview: 'rdp-day--preview',
          }}
          classNames={{
            root: 'rdp-root rental-day-picker',
          }}
          aria-label={t('rental.calendar_label')}
        />
      </div>

      <div className="rental-date-picker__legend" aria-hidden="true">
        <span className="rental-date-picker__legend-item rental-date-picker__legend-item--unavailable">
          {t('rental.calendar_legend_unavailable')}
        </span>
        <span className="rental-date-picker__legend-item rental-date-picker__legend-item--selected">
          {t('rental.calendar_legend_selected')}
        </span>
      </div>

      <p className="rental-date-picker__horizon text-muted">
        {t('rental.booking_horizon_hint', { days: MAX_RENTAL_END_DAYS_AHEAD })}
      </p>

      <FieldError id="rental-date-error" message={rangeError} />
    </div>
  );
};
